import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Building2, CalendarCheck, CalendarX, CloudDownload, FileCheck2, FileText, Package, RefreshCw, ShoppingCart, Truck,
  type LucideIcon,
} from 'lucide-react'
import api from '../services/api'
import { useEmpresa } from '../contexts/EmpresaContext'

// Painel inicial de Compras: período contábil aberto (TFin005), notas pendentes de lançamento
// (manifestadores) e um resumo do que foi comprado no período.

interface QtdValor { qtd: number; valor: number }
interface Painel {
  periodoContabil: { ini: string | null; fim: string | null }
  pendentes: Record<'nfe' | 'nfse' | 'cte', { atual: QtdValor; anterior: QtdValor }>
  paraImportar: { nfe: number; cte: number; nfse: number; total: number }
  comprado: { atual: QtdValor; anterior: QtdValor }
  fornecedores: { cliCod: string; nome: string; notas: number; valor: number }[]
  itens: { itsCod: number; nome: string; qtd: number; un: string; valor: number }[]
  atualizadoEm: string
}

type Preset = 'mes' | 'mesPassado' | 'tresMeses' | 'ano'
const PRESETS: { id: Preset; rotulo: string; anterior: string }[] = [
  { id: 'mes', rotulo: 'Este mês', anterior: 'Mês anterior' },
  { id: 'mesPassado', rotulo: 'Mês passado', anterior: 'Mês retrasado' },
  { id: 'tresMeses', rotulo: 'Últimos 3 meses', anterior: '3 meses antes' },
  { id: 'ano', rotulo: 'Este ano', anterior: 'Ano passado' },
]

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
function periodos(p: Preset) {
  const h = new Date(), y = h.getFullYear(), m = h.getMonth()
  const fimMes = (yy: number, mm: number) => new Date(yy, mm + 1, 0)
  switch (p) {
    case 'mesPassado': return { ini: iso(new Date(y, m - 1, 1)), fim: iso(fimMes(y, m - 1)), antIni: iso(new Date(y, m - 2, 1)), antFim: iso(fimMes(y, m - 2)) }
    case 'tresMeses': return { ini: iso(new Date(y, m - 2, 1)), fim: iso(h), antIni: iso(new Date(y, m - 5, 1)), antFim: iso(fimMes(y, m - 3)) }
    case 'ano': return { ini: `${y}-01-01`, fim: iso(h), antIni: `${y - 1}-01-01`, antFim: `${y - 1}-12-31` }
    default: return { ini: iso(new Date(y, m, 1)), fim: iso(h), antIni: iso(new Date(y, m - 1, 1)), antFim: iso(fimMes(y, m - 1)) }
  }
}

const br = (s: string | null) => (s ? s.split('-').reverse().join('/') : '')
const moeda = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const numero = (v: number, casas = 0) => v.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas })

const card: React.CSSProperties = {
  background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 1px 3px rgba(15,23,42,.06)', padding: '1rem 1.1rem',
}

function CardPendente({ titulo, icon: Icon, cor, atual, anterior, rotuloAnterior, onClick }: {
  titulo: string; icon: LucideIcon; cor: string; atual: QtdValor; anterior: QtdValor; rotuloAnterior: string; onClick: () => void
}) {
  return (
    <button type="button" onClick={onClick} className="cmp-card" title="Ver as notas pendentes"
      style={{ ...card, textAlign: 'left', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem' }}>
        <span style={{ width: 34, height: 34, borderRadius: 8, background: `${cor}14`, color: cor, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={19} />
        </span>
        <span style={{ fontWeight: 700, fontSize: '0.88rem', color: '#0f172a' }}>{titulo}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1rem' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '2.1rem', fontWeight: 800, color: cor, lineHeight: 1 }}>{atual.qtd}</div>
          <div style={{ fontSize: '0.78rem', color: '#475569', marginTop: 4 }}>{moeda(atual.valor)}</div>
        </div>
        <div style={{ borderLeft: '1px solid #e2e8f0', paddingLeft: '0.8rem', minWidth: 96 }}>
          <div style={{ fontSize: '0.66rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{rotuloAnterior}</div>
          <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#334155' }}>{anterior.qtd}</div>
          <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{moeda(anterior.valor)}</div>
        </div>
      </div>
    </button>
  )
}

function Ranking({ titulo, icon: Icon, linhas, vazio }: {
  titulo: string; icon: LucideIcon; vazio: string
  linhas: { chave: string; nome: string; detalhe: string; valor: number }[]
}) {
  const max = Math.max(...linhas.map(l => l.valor), 1)
  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.75rem', fontWeight: 700, color: '#1b3f7b', textTransform: 'uppercase', marginBottom: '0.75rem' }}>
        <Icon size={15} /> {titulo}
      </div>
      {linhas.length === 0 && <div style={{ color: '#94a3b8', fontSize: '0.83rem' }}>{vazio}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
        {linhas.map((l, i) => (
          <div key={l.chave}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: '0.82rem' }}>
              <span style={{ color: '#0f172a', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={l.nome}>
                <span style={{ color: '#94a3b8', fontWeight: 500, marginRight: 6 }}>{i + 1}.</span>{l.nome}
              </span>
              <span style={{ color: '#0f172a', fontWeight: 700, whiteSpace: 'nowrap' }}>{moeda(l.valor)}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              <div style={{ flex: 1, height: 6, background: '#eef2f7', borderRadius: 3 }}>
                <div style={{ width: `${Math.max(3, (l.valor / max) * 100)}%`, height: '100%', background: '#1a6ea8', borderRadius: 3 }} />
              </div>
              <span style={{ fontSize: '0.7rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>{l.detalhe}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default function Compras() {
  const navigate = useNavigate()
  const { empresaCod } = useEmpresa()
  const [preset, setPreset] = useState<Preset>('mes')
  const [dados, setDados] = useState<Painel | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState('')

  const per = useMemo(() => periodos(preset), [preset])
  const rotuloAnterior = PRESETS.find(p => p.id === preset)!.anterior

  const carregar = useCallback(() => {
    setCarregando(true); setErro('')
    api.get('/erp/compras/dashboard', { params: { empcod: empresaCod ?? 1, ...per } })
      .then(r => setDados(r.data))
      .catch(e => setErro(e?.response?.data?.error || 'Erro ao carregar o painel.'))
      .finally(() => setCarregando(false))
  }, [empresaCod, per])

  useEffect(carregar, [carregar])

  // Abre o manifestador já filtrado só nas pendentes do período (mesmo critério do card)
  function abrirPendentes(tipo: 'nfe' | 'nfse' | 'cte') {
    const q = new URLSearchParams({ de: per.ini, ate: per.fim, digitada: 'nao' })
    if (tipo !== 'nfse') q.set('situacao', '1')
    if (tipo === 'cte') q.set('papel', 'tomador')
    navigate(`/compras/manifestacao/${tipo}?${q}`)
  }

  // Situação do período contábil hoje
  const hoje = iso(new Date())
  const pc = dados?.periodoContabil
  const aberto = !!pc?.ini && hoje >= pc.ini && (!pc.fim || hoje <= pc.fim)
  const diasRestantes = pc?.fim ? Math.round((new Date(pc.fim + 'T00:00').getTime() - new Date(hoje + 'T00:00').getTime()) / 86400000) : null

  const variacao = dados && dados.comprado.anterior.valor > 0
    ? ((dados.comprado.atual.valor - dados.comprado.anterior.valor) / dados.comprado.anterior.valor) * 100 : null

  return (
    <div style={{ maxWidth: 1280 }}>
      {/* Cabeçalho */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.45rem', fontWeight: 800, color: '#0f172a' }}>Compras</h2>
          <div style={{ fontSize: '0.82rem', color: '#64748b' }}>Acompanhe as notas pendentes de lançamento e o volume de compras.</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.75rem' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.7rem', fontWeight: 700, color: '#64748b' }}>
            Período
            <select value={preset} onChange={e => setPreset(e.target.value as Preset)}
              style={{ height: 34, border: '1px solid #d1d5db', borderRadius: 6, padding: '0 0.6rem', fontSize: '0.84rem', background: '#fff', minWidth: 150 }}>
              {PRESETS.map(p => <option key={p.id} value={p.id}>{p.rotulo}</option>)}
            </select>
          </label>
          <div style={{ fontSize: '0.7rem', color: '#94a3b8', textAlign: 'right', paddingBottom: 2 }}>
            Última atualização<br />
            <span style={{ color: '#475569' }}>{dados ? new Date(dados.atualizadoEm).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</span>
          </div>
          <button type="button" className="btn btn-secondary" onClick={carregar} disabled={carregando} title="Atualizar"
            style={{ height: 34, width: 34, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <RefreshCw size={15} className={carregando ? 'girando' : undefined} />
          </button>
        </div>
      </div>

      {erro && <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>{erro}</div>}

      {/* Período aberto para lançamento — em destaque */}
      {dados && (
        <div style={{
          borderRadius: 12, padding: '1.1rem 1.4rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '1.2rem', flexWrap: 'wrap',
          background: aberto ? 'linear-gradient(135deg, #1b3f7b, #1a6ea8)' : 'linear-gradient(135deg, #9a3412, #c2410c)', color: '#fff',
          boxShadow: '0 4px 14px rgba(27,63,123,.18)',
        }}>
          <span style={{ width: 54, height: 54, borderRadius: 12, background: 'rgba(255,255,255,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {aberto ? <CalendarCheck size={30} /> : <CalendarX size={30} />}
          </span>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.85 }}>
              Período aberto para lançamento de notas fiscais
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 800, lineHeight: 1.15, marginTop: 2 }}>
              {pc?.ini ? `${br(pc.ini)}  a  ${pc.fim ? br(pc.fim) : 'em aberto'}` : 'Não definido'}
            </div>
            <div style={{ fontSize: '0.78rem', opacity: 0.85, marginTop: 3 }}>
              {!pc?.ini
                ? 'Defina o Período Permitido p/ Lançamento Contábil nos Parâmetros do Financeiro (TFin005 · Contabilidade).'
                : aberto
                  ? 'Notas com data de lançamento fora desse período não podem ser lançadas.'
                  : 'Hoje está fora do período permitido — novas notas não podem ser lançadas até o período ser ajustado.'}
            </div>
          </div>
          {aberto && diasRestantes !== null && (
            <div style={{ textAlign: 'center', background: 'rgba(255,255,255,.12)', borderRadius: 10, padding: '0.55rem 1rem' }}>
              <div style={{ fontSize: '1.7rem', fontWeight: 800, lineHeight: 1 }}>{diasRestantes}</div>
              <div style={{ fontSize: '0.7rem', opacity: 0.85 }}>{diasRestantes === 1 ? 'dia para fechar' : 'dias para fechar'}</div>
            </div>
          )}
        </div>
      )}

      {carregando && !dados && <div style={{ color: '#64748b', fontSize: '0.88rem' }}>Carregando painel...</div>}

      {dados && (
        <>
          {/* Pendências */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0.9rem', marginBottom: '1rem', opacity: carregando ? 0.6 : 1 }}>
            <CardPendente titulo="NF-e 55 pendentes" icon={FileText} cor="#dc2626" rotuloAnterior={rotuloAnterior}
              atual={dados.pendentes.nfe.atual} anterior={dados.pendentes.nfe.anterior} onClick={() => abrirPendentes('nfe')} />
            <CardPendente titulo="NFS-e pendentes" icon={FileCheck2} cor="#2563eb" rotuloAnterior={rotuloAnterior}
              atual={dados.pendentes.nfse.atual} anterior={dados.pendentes.nfse.anterior} onClick={() => abrirPendentes('nfse')} />
            <CardPendente titulo="CT-e 57 pendentes" icon={Truck} cor="#16a34a" rotuloAnterior={rotuloAnterior}
              atual={dados.pendentes.cte.atual} anterior={dados.pendentes.cte.anterior} onClick={() => abrirPendentes('cte')} />
            <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: '0.6rem' }}
              title="Documentos recebidos e ainda não lançados, dentro do período aberto para lançamento">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem' }}>
                <span style={{ width: 34, height: 34, borderRadius: 8, background: '#f1f5f9', color: '#475569', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CloudDownload size={19} />
                </span>
                <span style={{ fontWeight: 700, fontSize: '0.88rem', color: '#0f172a' }}>Documentos para importar</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ fontSize: '2.1rem', fontWeight: 800, color: '#0f172a', lineHeight: 1, flex: 1 }}>{dados.paraImportar.total}</div>
                <div style={{ fontSize: '0.78rem', color: '#475569', display: 'grid', gridTemplateColumns: 'auto auto', columnGap: '1rem', rowGap: 2 }}>
                  <span>NF-e 55</span><b style={{ textAlign: 'right' }}>{dados.paraImportar.nfe}</b>
                  <span>CT-e 57</span><b style={{ textAlign: 'right' }}>{dados.paraImportar.cte}</b>
                  <span>NFS-e</span><b style={{ textAlign: 'right' }}>{dados.paraImportar.nfse}</b>
                </div>
              </div>
              <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Dentro do período aberto para lançamento</div>
            </div>
          </div>

          {/* Resumo das compras */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '0.9rem', opacity: carregando ? 0.6 : 1 }}>
            <div style={{ ...card, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '0.8rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.75rem', fontWeight: 700, color: '#1b3f7b', textTransform: 'uppercase' }}>
                <ShoppingCart size={15} /> Total comprado
              </div>
              <div>
                <div style={{ fontSize: '1.9rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.1 }}>{moeda(dados.comprado.atual.valor)}</div>
                <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 4 }}>{numero(dados.comprado.atual.qtd)} notas lançadas</div>
              </div>
              <div style={{ borderTop: '1px solid #eef2f7', paddingTop: '0.6rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.78rem', color: '#64748b' }}>
                <span>{rotuloAnterior}: <b style={{ color: '#334155' }}>{moeda(dados.comprado.anterior.valor)}</b></span>
                {variacao !== null && (
                  <span style={{ fontWeight: 700, color: variacao > 0 ? '#b91c1c' : '#15803d' }}>
                    {variacao > 0 ? '▲' : '▼'} {numero(Math.abs(variacao), 1)}%
                  </span>
                )}
              </div>
              <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>Pela data de recebimento · {br(per.ini)} a {br(per.fim)}</div>
            </div>

            <Ranking titulo="Principais fornecedores" icon={Building2} vazio="Nenhuma compra lançada no período."
              linhas={dados.fornecedores.map(f => ({ chave: f.cliCod, nome: f.nome, valor: f.valor, detalhe: `${f.notas} ${f.notas === 1 ? 'nota' : 'notas'}` }))} />

            <Ranking titulo="Itens mais comprados" icon={Package} vazio="Nenhum item lançado no período."
              linhas={dados.itens.map(i => ({ chave: String(i.itsCod), nome: i.nome, valor: i.valor, detalhe: `${numero(i.qtd, i.qtd % 1 ? 2 : 0)} ${i.un}` }))} />
          </div>
        </>
      )}
      <style>{`
        .cmp-card { transition: box-shadow .15s, transform .15s; border: 1px solid #e2e8f0; }
        .cmp-card:hover { box-shadow: 0 6px 18px rgba(15,23,42,.10); transform: translateY(-1px); }
        .girando { animation: cmp-gira 0.9s linear infinite; }
        @keyframes cmp-gira { to { transform: rotate(360deg) } }
      `}</style>
    </div>
  )
}
